import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CaretRight, Phone, WhatsappLogo } from "@phosphor-icons/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO_PAGES } from "@/constants/seoPages";
import { PROJECT_PAGES } from "@/constants/projectPages";
import { PROJECTS, STATUS_STYLES, slug } from "@/constants/projects";
import { SITE, WA_LINK, IMAGES } from "@/constants/site";
import LeadForm from "@/components/landing/LeadForm";
import NotFound from "@/pages/NotFound";

const DOMAIN = "https://www.vrahmedabadrealtors.com";
const DEFAULT_TITLE =
  "VR Ahmedabad Realtors | Luxury Real Estate in Ahmedabad | Property Consultants";
const DEFAULT_META =
  "VR Ahmedabad Realtors is a luxury real estate advisory in Ahmedabad offering premium apartments, villas, commercial property and investment opportunities across Ahmedabad, Adani Shantigram and GIFT City.";

function setHead(page) {
  const path = page.path;
  const canonicalUrl = `${DOMAIN}${path}`;
  document.title = page.title;
  const setMeta = (attr, key, content) => {
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
    return el;
  };
  const prevMeta = document.querySelector("meta[name='description']")?.content;
  setMeta("name", "description", page.meta);
  setMeta("property", "og:title", page.title);
  setMeta("property", "og:description", page.meta);
  setMeta("property", "og:url", canonicalUrl);
  setMeta("name", "twitter:title", page.title);
  setMeta("name", "twitter:description", page.meta);
  let canonical = document.querySelector("link[rel='canonical']");
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  const prevCanonical = canonical.getAttribute("href");
  canonical.setAttribute("href", canonicalUrl);
  const ld = document.createElement("script");
  ld.type = "application/ld+json";
  ld.id = "seo-page-ld";
  ld.text = JSON.stringify([
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [...page.crumbs, { label: page.h1, href: path }].map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.label,
        item: `${DOMAIN}${c.href}`,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ]);
  document.head.appendChild(ld);
  window.scrollTo(0, 0);
  return () => {
    document.title = DEFAULT_TITLE;
    const meta = document.querySelector("meta[name='description']");
    if (meta && prevMeta) meta.content = prevMeta;
    if (prevCanonical) canonical.setAttribute("href", prevCanonical);
    document.getElementById("seo-page-ld")?.remove();
  };
}

export default function SeoLandingPage() {
  const { slug: pageSlug, projectSlug } = useParams();
  const lookup = projectSlug || pageSlug;
  const source = projectSlug ? PROJECT_PAGES : SEO_PAGES;
  const page = source.find((p) => p.slug === lookup);

  useEffect(() => {
    if (!page) return;
    return setHead(page);
  }, [page]);

  if (!page) return <NotFound />;

  const cards = PROJECTS.filter((p) => (page.projects || []).includes(p.name));
  const scrollToEnquire = () => document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" });

  return (
    <main data-testid={`seo-page-${page.slug}`} className="bg-cream font-sans text-slate-800 antialiased">
      <header className="border-b border-black/5 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
          <Link to="/" data-testid="seo-nav-brand" className="flex items-center gap-3">
            <img src="/vr-logo.jpeg" alt="VR Ahmedabad Realtors logo" width="44" height="44" className="h-11 w-11 rounded-sm object-cover ring-1 ring-gold/50" />
            <span className="leading-tight">
              <span className="block font-serif text-base tracking-tight text-slate-900">VR Ahmedabad Realtors</span>
              <span className="block text-[10px] uppercase tracking-[0.25em] text-forest/70">Luxury Real Estate · Ahmedabad</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <a href={`tel:${SITE.phoneRaw}`} data-testid="seo-call-link" className="hidden items-center gap-2 text-sm font-semibold text-slate-800 md:flex">
              <Phone size={16} weight="fill" className="text-gold" />
              {SITE.phoneDisplay}
            </a>
            <button
              data-testid="seo-nav-enquire-btn"
              onClick={scrollToEnquire}
              className="rounded-sm bg-gold px-5 py-2.5 text-sm font-bold tracking-wide text-forest transition-colors duration-200 hover:bg-[#d4b274]"
            >
              Enquire Now
            </button>
          </div>
        </div>
      </header>

      <section className="grain bg-forest py-20 lg:py-24">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12">
          <nav data-testid="breadcrumbs" aria-label="Breadcrumb" className="mb-8 flex flex-wrap items-center gap-2 text-xs text-white/50">
            {page.crumbs.map((c) => (
              <span key={c.href} className="flex items-center gap-2">
                <Link to={c.href} className="transition-colors duration-200 hover:text-gold">{c.label}</Link>
                <CaretRight size={10} />
              </span>
            ))}
            <span className="text-gold">{page.h1.split("–")[0].split("-")[0].trim()}</span>
          </nav>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">
            {page.kicker}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="mt-5 max-w-3xl font-serif text-4xl tracking-tighter text-white sm:text-5xl">
            {page.h1}
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }} className="mt-6 max-w-2xl space-y-4">
            {page.intro.map((para) => (
              <p key={para.slice(0, 24)} className="text-base leading-relaxed text-white/75">
                {para}
              </p>
            ))}
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }} className="mt-10 flex flex-wrap gap-4">
            <button
              data-testid="seo-hero-enquire-btn"
              onClick={scrollToEnquire}
              className="group flex items-center gap-2 rounded-sm bg-gold px-8 py-4 text-sm font-bold uppercase tracking-widest text-forest transition-colors duration-200 hover:bg-[#d4b274]"
            >
              Request Price & Availability
              <ArrowRight size={16} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="seo-hero-whatsapp-btn"
              className="flex items-center gap-2 rounded-sm border border-white/40 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors duration-200 hover:border-gold hover:text-gold"
            >
              <WhatsappLogo size={16} weight="fill" />
              WhatsApp a Property Advisor
            </a>
          </motion.div>
        </div>
      </section>

      {page.sections?.length > 0 && (
        <section className="bg-white py-20 lg:py-24">
          <div className="mx-auto max-w-4xl px-6 lg:px-12">
            {page.sections.map((s) => (
              <div key={s.h2} className="mb-12 last:mb-0">
                <h2 className="font-serif text-xl tracking-tight text-slate-900 sm:text-2xl">{s.h2}</h2>
                {s.paras?.map((p) => (
                  <p key={p.slice(0, 24)} className="mt-4 text-base leading-relaxed text-slate-600">
                    {p}
                  </p>
                ))}
                {s.bullets && (
                  <ul className="mt-5 space-y-3">
                    {s.bullets.map((b) => (
                      <li key={b.slice(0, 24)} className="flex items-start gap-3 text-sm text-slate-700">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {cards.length > 0 && (
        <section className="py-20 lg:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <h2 className="font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl">Available options</h2>
            <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {cards.map((p) => (
                <article key={p.name} data-testid={`seo-project-card-${slug(p.name)}`} className="group flex flex-col overflow-hidden rounded-lg border border-black/5 bg-white">
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={p.img}
                      alt={`${p.name} — ${p.config} in Adani Shantigram, Ahmedabad`}
                      loading="lazy"
                      width="600"
                      height="400"
                      onError={(e) => {
                        if (e.currentTarget.src !== IMAGES.hero) e.currentTarget.src = IMAGES.hero;
                      }}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className={`absolute left-4 top-4 rounded-sm px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[p.status]}`}>
                      {p.status}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-serif text-xl text-slate-900">{p.name}</h3>
                    <p className="mt-1 text-sm text-slate-500">{p.config}</p>
                    {p.sizes && <p className="mt-1 text-xs text-slate-400">{p.sizes}</p>}
                    <div className="mt-6 flex items-center justify-between border-t border-black/5 pt-4">
                      <span className="text-sm font-bold text-forest">{p.price}</span>
                      <button
                        data-testid={`seo-project-enquire-${slug(p.name)}`}
                        onClick={scrollToEnquire}
                        className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-forest transition-colors duration-200 hover:text-gold"
                      >
                        Enquire About This Property
                        <ArrowRight size={14} weight="bold" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {page.related?.length > 0 && (
        <section data-testid="related-links" className="border-t border-black/5 bg-white py-14">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-forest/70">Explore further</h2>
            <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3">
              {page.related.map((r) => (
                <Link key={r.href} to={r.href} data-testid={`related-${slug(r.label)}`} className="text-sm font-medium text-forest transition-colors duration-200 hover:text-gold">
                  {r.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-12">
          <h2 className="font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-8 w-full">
            {page.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`seo-faq-${i}`} className="border-black/10">
                <AccordionTrigger data-testid={`seo-faq-trigger-${i}`} className="py-5 text-left font-serif text-base text-slate-900 hover:text-forest sm:text-lg">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`seo-faq-content-${i}`} className="text-sm leading-relaxed text-slate-600 sm:text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section id="enquire" className="bg-sand py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:grid-cols-12 lg:gap-16 lg:px-12">
          <div className="flex flex-col justify-center md:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-forest/70">Enquire</p>
            <h2 className="mt-4 font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl">
              Speak with a Shantigram specialist
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              Current availability, pricing, floor plans and site visits — share your details and we'll call you back
              shortly.
            </p>
            <a href={`tel:${SITE.phoneRaw}`} data-testid="seo-call-cta" className="mt-8 flex w-fit items-center gap-3 rounded-sm border border-forest/15 bg-white px-6 py-4 transition-colors duration-200 hover:border-gold">
              <Phone size={20} weight="fill" className="text-forest" />
              <span className="font-semibold text-slate-900">{SITE.phoneDisplay}</span>
            </a>
          </div>
          <div className="md:col-span-7">
            <LeadForm configuration="" onConfigurationChange={() => {}} project={page.project} />
          </div>
        </div>
      </section>

      <footer className="grain bg-forest py-12">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12">
          <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
            <Link to="/" data-testid="seo-footer-home-link" className="font-semibold text-gold hover:underline">
              ← VR Ahmedabad Realtors — Home
            </Link>
            <Link to="/adani-shantigram" className="text-white/70 transition-colors duration-200 hover:text-gold">Adani Shantigram</Link>
            <Link to="/insights" className="text-white/70 transition-colors duration-200 hover:text-gold">Property Guides</Link>
            <Link to="/about" className="text-white/70 transition-colors duration-200 hover:text-gold">About Us</Link>
            <Link to="/contact" className="text-white/70 transition-colors duration-200 hover:text-gold">Contact</Link>
          </div>
          <p className="mt-8 text-xs leading-relaxed text-white/40">
            Disclaimer: VR Ahmedabad Realtors is an independent real estate advisory. This is not the official website
            of Adani Realty. Project names, images and details are indicative and belong to their respective
            developers. Prices and availability are subject to change — please verify all details with our team.
          </p>
          <p className="mt-4 text-xs text-white/40">© {new Date().getFullYear()} VR Ahmedabad Realtors, Ahmedabad, Gujarat. All rights reserved.</p>
        </div>
      </footer>
    </main>
  );
}
